import {
    type MemberInput,
    type RetirementAccountType,
    type RetirementCalculatorInputs as RetirementCalculatorInputsData,
} from '@fruitful/financial-calculators';
import { roundToTwoDecimals } from '@fruitful/fruitils';
import { type TaxFiling } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';

import {
    type RetirementCalculatorFormData,
    retirementCalculatorFormSchema,
} from '../types';
import { RetirementCalculatorHouseholdInputs } from './RetirementCalculatorHouseholdInputs';
import { RetirementCalculatorMemberInputs } from './RetirementCalculatorMemberInputs';

interface RetirementCalculatorInputsProps {
    onCalculate: (inputs: RetirementCalculatorInputsData) => void;
    isCalculating: boolean;
    initialData: RetirementCalculatorInputsData;
}

export const RetirementCalculatorInputs = ({
    onCalculate,
    isCalculating,
    initialData,
}: RetirementCalculatorInputsProps) => {
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Transform initialData to form format if provided
    const getDefaultValues = () => {
        const primaryMember = initialData.memberInputs[0];
        const partnerMember = initialData.memberInputs[1];

        if (!primaryMember) {
            throw new Error('No primary member found for membership');
        }

        // Helper function to map member data to form format
        const mapMemberToFormData = (member: MemberInput) => ({
            currentAge: member.currentAge.toString(),
            retirementAge: member.retirementAge.toString(),
            socialSecurityBenefitAge:
                member.socialSecurityBenefitAge.toString(),
            lifeExpectancy: member.lifeExpectancy.toString(),
            currentGrossAnnualIncome:
                member.currentGrossAnnualIncome.toString(),
            hasEmployerRetirementPlan: member.hasEmployerRetirementPlan,
            employerMatchMaximumPercentOfIncome:
                member.employerMatchMaximumPercentOfIncome != null
                    ? roundToTwoDecimals(
                          member.employerMatchMaximumPercentOfIncome * 100,
                      ).toString()
                    : '',
            employerMatchPercentage:
                member.employerMatchPercentage != null
                    ? roundToTwoDecimals(
                          member.employerMatchPercentage * 100,
                      ).toString()
                    : '',
            isSelfEmployed: member.isSelfEmployed,
            annualSocialSecurityBenefit:
                member.annualSocialSecurityBenefit?.toString() ?? '',
            includeRetirementHealthcareCosts:
                member.includeRetirementHealthcareCosts,
            longTermCareCost: member.longTermCareCost.toString(),
            retirementHealthcareCost:
                member.retirementHealthcareCost.toString(),
            accounts: member.currentRetirementSavings.map((account) => ({
                id: account.id,
                type: account.type,
                balance: account.balance.toString(),
                equitiesPercentage: roundToTwoDecimals(
                    account.equitiesPercentage * 100,
                ).toString(),
                costBasis: account.costBasis.toString(), // Convert to string
            })),
        });

        const baseValues = {
            householdInputs: {
                annualSpendingInRetirement:
                    initialData.householdInputs.annualSpendingInRetirement.toString(),
                targetSuccessLikelihood: roundToTwoDecimals(
                    initialData.householdInputs.targetSuccessLikelihood * 100,
                ).toString(),
                taxFilingStatus: initialData.householdInputs.taxFilingStatus,
                residentialLocation:
                    initialData.householdInputs.residentialLocation,
                currentMarginalTaxRate:
                    initialData.householdInputs.currentMarginalTaxRate != null
                        ? roundToTwoDecimals(
                              initialData.householdInputs
                                  .currentMarginalTaxRate * 100,
                          ).toString()
                        : '',
                retirementContributionWaterfall:
                    initialData.householdInputs.retirementContributionWaterfall?.map(
                        (step) => ({
                            memberId: step.memberId,
                            contributions: Object.fromEntries(
                                Object.entries(step.contributions).map(
                                    ([accountType, percentage]) => [
                                        accountType,
                                        roundToTwoDecimals(
                                            (percentage ?? 0) * 100,
                                        ).toString(), // Convert decimal to whole number string
                                    ],
                                ),
                            ),
                        }),
                    ) ?? [],
            },
            primaryMember: mapMemberToFormData(primaryMember),
        };

        if (partnerMember) {
            return {
                ...baseValues,
                partnerMember: mapMemberToFormData(partnerMember),
            };
        }

        return baseValues;
    };

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<RetirementCalculatorFormData>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(retirementCalculatorFormSchema),
        defaultValues: getDefaultValues(),
    });

    // Helper function to map form data back to member input shape
    const mapFormDataToMemberInput = (
        formData:
            | RetirementCalculatorFormData['primaryMember']
            | RetirementCalculatorFormData['partnerMember'],
        memberId: string,
    ): MemberInput => {
        if (!formData) {
            throw new Error('Form data is required for member input mapping');
        }

        return {
            id: memberId,
            currentAge: parseInt(formData.currentAge),
            retirementAge: parseInt(formData.retirementAge),
            socialSecurityBenefitAge: parseInt(
                formData.socialSecurityBenefitAge,
            ),
            lifeExpectancy: parseInt(formData.lifeExpectancy),
            currentGrossAnnualIncome: parseFloat(
                formData.currentGrossAnnualIncome,
            ),
            currentRetirementSavings: formData.accounts.map((account) => ({
                id: account.id,
                owner: memberId,
                type: account.type as RetirementAccountType,
                balance: parseFloat(account.balance),
                equitiesPercentage:
                    parseFloat(account.equitiesPercentage) / 100, // Convert percentage to decimal
                costBasis: parseFloat(account.costBasis),
            })),
            hasEmployerRetirementPlan: formData.hasEmployerRetirementPlan,
            employerMatchMaximumPercentOfIncome:
                formData.employerMatchMaximumPercentOfIncome != null
                    ? roundToTwoDecimals(
                          parseFloat(
                              formData.employerMatchMaximumPercentOfIncome,
                          ) / 100,
                      ) // Convert percentage to decimal
                    : undefined,
            employerMatchPercentage:
                formData.employerMatchPercentage != null
                    ? roundToTwoDecimals(
                          parseFloat(formData.employerMatchPercentage) / 100,
                      ) // Convert percentage to decimal
                    : undefined,
            isSelfEmployed: formData.isSelfEmployed,
            annualSocialSecurityBenefit:
                formData.annualSocialSecurityBenefit != null
                    ? parseFloat(formData.annualSocialSecurityBenefit)
                    : undefined,
            includeRetirementHealthcareCosts:
                formData.includeRetirementHealthcareCosts,
            longTermCareCost: parseFloat(formData.longTermCareCost),
            retirementHealthcareCost: parseFloat(
                formData.retirementHealthcareCost,
            ),
        };
    };

    const onSubmit: SubmitHandler<RetirementCalculatorFormData> = (data) => {
        // Transform form data to match the expected input format
        const primaryMemberId = initialData.memberInputs[0]?.id;
        const partnerMemberId = initialData.memberInputs[1]?.id;

        if (primaryMemberId == null) {
            throw new Error('No primary member found for membership');
        }

        const transformedData: RetirementCalculatorInputsData = {
            householdInputs: {
                annualSpendingInRetirement: parseFloat(
                    data.householdInputs.annualSpendingInRetirement,
                ),
                targetSuccessLikelihood: roundToTwoDecimals(
                    parseFloat(data.householdInputs.targetSuccessLikelihood) /
                        100,
                ), // Convert percentage to decimal
                taxFilingStatus: data.householdInputs
                    .taxFilingStatus as TaxFiling,
                residentialLocation: data.householdInputs.residentialLocation,
                currentMarginalTaxRate:
                    data.householdInputs.currentMarginalTaxRate != null
                        ? roundToTwoDecimals(
                              parseFloat(
                                  data.householdInputs.currentMarginalTaxRate,
                              ) / 100,
                          ) // Convert percentage to decimal
                        : undefined,
                retirementContributionWaterfall:
                    data.householdInputs.retirementContributionWaterfall?.map(
                        (step) => ({
                            memberId: step.memberId,
                            contributions: Object.fromEntries(
                                Object.entries(step.contributions).map(
                                    ([accountType, percentage]) => [
                                        accountType,
                                        roundToTwoDecimals(
                                            parseFloat(percentage) / 100,
                                        ), // Convert whole number string to decimal
                                    ],
                                ),
                            ),
                        }),
                    ) ?? undefined,
            },
            memberInputs: [
                mapFormDataToMemberInput(data.primaryMember, primaryMemberId),
                ...(data.partnerMember != null && partnerMemberId != null
                    ? [
                          mapFormDataToMemberInput(
                              data.partnerMember,
                              partnerMemberId,
                          ),
                      ]
                    : []),
            ],
        };

        onCalculate(transformedData);
    };

    return (
        <YStack width="90vw" maxWidth={800} height="100%">
            <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => {
                    setActiveTabIndex(e.index);
                }}
                style={{
                    overflow: 'scroll',
                }}
            >
                <TabPanel header="Household">
                    <RetirementCalculatorHouseholdInputs
                        control={control}
                        register={register}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        memberIds={initialData.memberInputs.map(
                            (member) => member.id,
                        )}
                    />
                </TabPanel>

                <TabPanel header="Primary Member">
                    <RetirementCalculatorMemberInputs
                        memberPath="primaryMember"
                        title="Primary Member"
                        control={control}
                        register={register}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                    />
                </TabPanel>

                {initialData.memberInputs[1] && (
                    <TabPanel header="Partner Member">
                        <RetirementCalculatorMemberInputs
                            memberPath="partnerMember"
                            title="Partner Member"
                            control={control}
                            register={register}
                            errors={errors}
                            watch={watch}
                            setValue={setValue}
                        />
                    </TabPanel>
                )}
            </TabView>

            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text={isCalculating ? 'Calculating...' : 'Calculate'}
                onPress={handleSubmit(onSubmit)}
                disabled={isCalculating || !isValid}
            />
        </YStack>
    );
};
